import { useCallback, useEffect, useRef, useState } from "react";

export type UsePrompt<P, R> = {
  /** Initiates a Promise that can be resolved via the resolve() function. */
  prompt: (input: P) => Promise<R | null>;
  /** Resolves the Promise with the given response. No-op if the Promise is not active. */
  resolve: (response?: R | null) => void;
  /** The props passed to the prompt() function. Undefined when the Promise is not active. */
  promptProps: P | undefined;
};

/**
 * Hook for initiating an asynchronous operation that can be resolved via a
 * callback.  Use this to display a dialog or modal into which the `resolve()`
 * callback can be embedded.
 */
export function usePrompt<P, R>(): UsePrompt<P, R> {
  const [promptProps, setPromptProps] = useState<P>();
  const resolveRef = useRef<(response: R | null) => void>();

  const prompt = useCallback((props: P): Promise<R | null> => {
    setPromptProps(props);
    return new Promise((resolve) => {
      if (resolveRef.current) {
        // Resolve any pending promise if outstanding.
        resolveRef.current(null);
      }
      resolveRef.current = resolve;
    });
  }, []);

  const resolve = useCallback((response?: R | null) => {
    if (resolveRef.current) {
      setPromptProps(undefined);
      resolveRef.current(response ?? null);
      resolveRef.current = undefined;
    }
  }, []);

  // Cleanup: Resolve any pending Promise on unmount to prevent leaks.
  useEffect(() => {
    return () => {
      if (resolveRef.current) {
        resolveRef.current(null);
        resolveRef.current = undefined;
      }
    };
  }, []);

  return {
    prompt,
    resolve,
    promptProps,
  };
}
